import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

function VerificationPage() {
  const [searchParams] = useSearchParams();
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicationInfo, setApplicationInfo] = useState(null);
  const [providerInfo, setProviderInfo] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [clipboardChecked, setClipboardChecked] = useState(false);

  useEffect(() => {
    const handleVerification = async () => {
      try {
        const template = searchParams.get('template');
        
        if (!template) {
          setLoading(false);
          return;
        }

        // Decode the template parameter
        const decodedTemplate = decodeURIComponent(template);
        
        // Parse the JSON template
        const parsedTemplate = JSON.parse(decodedTemplate);
        setTemplateData(parsedTemplate);

        // Fetch application information
        try {
          const appResponse = await fetch(`https://api.reclaimprotocol.org/api/applications/info/${parsedTemplate.applicationId}`, {
            headers: {
              "accept": "*/*",
              "accept-language": "en-US,en;q=0.9",
              "sec-fetch-dest": "empty",
              "sec-fetch-mode": "cors",
              "sec-fetch-site": "same-site"
            },
            method: "GET",
            mode: "cors",
            credentials: "omit"
          });
          
          const appData = await appResponse.json();
          if (appData.isSuccess) {
            setApplicationInfo(appData.application);
          }
        } catch (appErr) {
          console.error('Error fetching application info:', appErr);
        }

        // Fetch provider information
        try {
          const providerResponse = await fetch(`https://api.reclaimprotocol.org/api/applications/${parsedTemplate.applicationId}/provider/${parsedTemplate.providerId}`, {
            headers: {
              "accept": "*/*",
              "accept-language": "en-GB,en-US;q=0.9,en;q=0.8",
              "content-type": "application/json",
              "sec-fetch-dest": "empty",
              "sec-fetch-mode": "cors",
              "sec-fetch-site": "same-site"
            },
            body: JSON.stringify({
              signature: parsedTemplate.signature,
              timestamp: parsedTemplate.timestamp
            }),
            method: "POST",
            mode: "cors",
            credentials: "omit"
          });
          
          const providerData = await providerResponse.json();
          if (providerData.isSucces) {
            setProviderInfo(providerData.providers.httpProvider[0]);
          }
        } catch (providerErr) {
          console.error('Error fetching provider info:', providerErr);
        }

        console.log('Verification template:', parsedTemplate);
        
        // Check if running on Android
        const androidCheck = /Android/i.test(navigator.userAgent);
        setIsAndroid(androidCheck);
        
        // Check if Reclaim app is installed
        checkAppInstalled();
        
        // Check clipboard on Android
        
        // Simulate processing time
        setTimeout(() => {
          setLoading(false);
        }, 1000);

      } catch (err) {
        console.error('Error parsing template:', err);
        setLoading(false);
      }
    };

    handleVerification();
  }, [searchParams]);

  const checkAppInstalled = () => {
    // Check if running on mobile
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Try to open the app using intent URL
         
      const intentUrl = `reclaimverifier://share.reclaimprotocol.org/verify?template=${encodeURIComponent(searchParams.get('template'))}`;
      
      // Create a hidden iframe to test if app is installed
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = intentUrl;
      
      let timeout;
      const checkInstalled = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          // If timeout occurs, app is not installed
          setIsAppInstalled(false);
          document.body.removeChild(iframe);
        }, 2000);
      };
      
      iframe.onload = () => {
        clearTimeout(timeout);
        setIsAppInstalled(true);
        document.body.removeChild(iframe);
      };
      
      document.body.appendChild(iframe);
      checkInstalled();
    } else {
      // On desktop, assume app is not installed
      setIsAppInstalled(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <h2>Processing Verification...</h2>
        <p>Please wait while we process your verification request.</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <h2 style={{ color: '#dc3545' }}>❌ Verification Error</h2>
        <p style={{ color: '#6c757d', marginBottom: '20px' }}>{error}</p>
        <button 
          onClick={() => window.location.href = '/'}
          style={{ 
            color: '#007bff', 
            textDecoration: 'none', 
            padding: '10px 20px', 
            border: '1px solid #007bff', 
            borderRadius: '5px',
            background: 'none',
            cursor: 'pointer'
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  const checkClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const urlData = window.location.href?.split('/verify')[1];
      
      if (clipboardText && clipboardText.includes(urlData)) {
        setClipboardChecked(true);
        return true;
      } else {
        setClipboardChecked(true);
        return false;
      }
    } catch (error) {
      setClipboardChecked(true);
      return false;
    }
  };

  const copyToClipboard = async () => {
    try {
      const urlData = window.location.href?.split('/verify')[1];
      await navigator.clipboard.writeText(urlData);
      setClipboardChecked(true);
      alert('URL copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy to clipboard. Please copy manually.');
    }
  };

  const handleContinue = async() => {
    const template = searchParams.get('template');
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const encodedTemplate = encodeURIComponent(template);
    const encodedUrl = encodeURIComponent(`https://share.reclaimprotocol.org/verifier?template=${encodedTemplate}`);

        // copy data after /verify
        const urlData = window.location.href?.split('/verify')[1]

                // copy the url from /verify

    

    if (isMobile) {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        // Directly show App Store URL for iOS, do not use universal link
        // alert saying testing for android - ios will have app clip 
        alert('Testing for android - ios will have app clip :)')
      } else {
        // Android: use intent:// URL, which will auto-redirect to Play Store if app is not installed
        if (window.navigator.getInstalledRelatedApps) {
          navigator.getInstalledRelatedApps().then((apps) => {
            // Detect if org.reclaimprotocol.app is installed
            const isReclaimAppInstalled = apps.some(
              (app) =>
                app.id === 'org.reclaimprotocol.app' ||
                (app.platform === 'play' && app.url && app.url.includes('org.reclaimprotocol.app'))
            );
            if (!isReclaimAppInstalled) {
              // App not installed, copy to clipboard
              window.navigator.clipboard.writeText(urlData);
            }
            // If installed, do nothing (or handle accordingly if needed)
          }).catch(() => {
            // Fallback: copy to clipboard if detection fails
          
          });
        } else {
          // Fallback for browsers that don't support getInstalledRelatedApps
        
        }
        const intentUrl = `intent://details?id=org.reclaimprotocol.app&template=${encodedTemplate}&url=${encodedUrl}&referrer=Z#Intent;scheme=market;action=android.intent.action.VIEW;package=com.android.vending;end;`;
        window.location.href = intentUrl;
      }
    } else {
      // On desktop, show QR code or redirect to mobile
      alert('Please use a mobile device to complete verification');
    }
  };

  return (
    <div style={{ 
      padding: '40px 20px', 
      textAlign: 'center',
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {/* App Logo */}
      {applicationInfo?.appImageUrl && (
        <img 
          src={applicationInfo.appImageUrl} 
          alt="App Logo" 
          style={{ 
            width: '60px', 
            height: '60px', 
            marginBottom: '24px',
            borderRadius: '12px'
          }} 
        />
      )}

      {/* Main Message */}
      <h1 style={{ 
        fontSize: '20px', 
        fontWeight: '600', 
        color: '#333',
        margin: '0 0 16px 0',
        lineHeight: '1.4',
        maxWidth: '300px'
      }}>
        {applicationInfo?.name || 'Reclaim Verifier'} needs to verify your data
      </h1>

      <p style={{ 
        fontSize: '14px', 
        color: '#666',
        margin: '0 0 16px 0',
        maxWidth: '280px',
        lineHeight: '1.5'
      }}>
        {isAppInstalled 
          ? 'Open the Reclaim app'
          : 'Open the Reclaim app to continue'
        }
      </p>

      {/* Android Clipboard Check */}

      {/* Action Button */}
      <button 
        onClick={async() => await handleContinue()}
        style={{
          padding: '16px 24px',
          fontSize: '16px',
          fontWeight: '600',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor:  'pointer',
          minWidth: '200px',
          transition: 'background-color 0.2s'
        }}
        onMouseOver={(e) => {
          if (!(isAndroid)) {
            e.target.style.backgroundColor = '#0056b3';
          }
        }}
        onMouseOut={(e) => {
          if (!(isAndroid)) {
            e.target.style.backgroundColor = '#007bff';
          }
        }}
      >
        {isAppInstalled ? 'Open Reclaim app to continue' : 'Continue with Reclaim app'}
      </button>
    </div>
  );
}

export default VerificationPage; 